import { useState } from 'react'
import {
  type AdminRole,
  roleHasUsers,
  roleIdFromSlug,
  useAdminRoles,
  useCreateRole,
  useDeleteRole,
  useUpdateRole,
} from '../../../features/admin/use-admin-roles'
import { AdminPage, adminError } from './admin-bits'
import { Button, ConfirmDialog, Dialog, EmptyRow, Field, Inline, inputCls, td, th } from './controls'
import { Card, mono } from './ui'

function RoleDialog({ role, onClose }: { role: AdminRole | null; onClose: () => void }) {
  const create = useCreateRole()
  const update = useUpdateRole()
  const [name, setName] = useState(role?.name ?? '')
  const [desc, setDesc] = useState(role?.description ?? '')
  const m = role ? update : create
  const submit = () => {
    const opts = { onSuccess: onClose }
    if (role) update.mutate({ roleId: role.id, name: name.trim(), description: desc.trim() }, opts)
    else create.mutate({ id: roleIdFromSlug(name), name: name.trim(), description: desc.trim() || undefined }, opts)
  }
  return (
    <Dialog
      open
      title={role ? 'Edit role' : 'Create a role'}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose} disabled={m.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={name.trim() === '' || m.isPending}>
            {m.isPending ? 'Saving...' : role ? 'Save' : 'Create role'}
          </Button>
        </>
      }
    >
      <Field label="Name">
        <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      {role ? null : (
        <p className="text-[12px] text-[var(--color-muted)]">
          Role id: <span style={mono}>{roleIdFromSlug(name)}</span>
        </p>
      )}
      <Field label="Description">
        <input className={inputCls} value={desc} onChange={(e) => setDesc(e.target.value)} />
      </Field>
      {m.error ? <Inline tone="bad">{adminError(m.error)}</Inline> : null}
    </Dialog>
  )
}

export function PorcelainAdminRoles() {
  const roles = useAdminRoles()
  const del = useDeleteRole()
  const [edit, setEdit] = useState<AdminRole | 'new' | null>(null)
  const [remove, setRemove] = useState<AdminRole | null>(null)
  const list = roles.data ?? []
  return (
    <AdminPage
      title="Roles"
      subtitle="Custom roles for this organization."
      loading={roles.isLoading}
      error={!!roles.error}
      right={<Button onClick={() => setEdit('new')}>Create role</Button>}
    >
      <Card title={`${list.length} role${list.length === 1 ? '' : 's'}`}>
        {list.length === 0 ? (
          <EmptyRow>No roles yet.</EmptyRow>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[14px]">
              <thead>
                <tr>
                  <th className={th}>Name</th>
                  <th className={th}>Id</th>
                  <th className={th}>Description</th>
                  <th className={th}>Users</th>
                  <th className={`${th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id} className="transition-colors hover:bg-[var(--v1-accent-soft)]">
                    <td className={`${td} font-medium`}>{r.name}</td>
                    <td className={td} style={mono}>
                      {r.id}
                    </td>
                    <td className={`${td} text-[var(--color-muted)]`}>{r.description ?? '-'}</td>
                    <td className={td} style={mono}>
                      {r.userCount}
                    </td>
                    <td className={`${td} text-right`}>
                      <div className="flex justify-end gap-1">
                        <Button variant="quiet" onClick={() => setEdit(r)}>
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          disabled={roleHasUsers(r)}
                          title={roleHasUsers(r) ? 'Reassign its users first' : undefined}
                          onClick={() => setRemove(r)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {edit ? <RoleDialog role={edit === 'new' ? null : edit} onClose={() => setEdit(null)} /> : null}
      <ConfirmDialog
        open={remove !== null}
        title="Delete this role?"
        body={<>The role {remove?.name} and its permission grants will be removed.</>}
        confirmLabel="Delete"
        danger
        pending={del.isPending}
        error={del.error ? adminError(del.error) : null}
        onConfirm={() => remove && del.mutate({ roleId: remove.id }, { onSuccess: () => setRemove(null) })}
        onClose={() => {
          del.reset()
          setRemove(null)
        }}
      />
    </AdminPage>
  )
}
